import { ListboxPopover, ListboxOption, ListboxInput, ListboxArrow, ListboxButton, ListboxList, useListboxContext } from '@reach/listbox';
import React__default, { forwardRef, useState, useCallback, useMemo, useRef, useEffect, Children } from 'react';
import { useTransition } from 'react-spring';
import styled from 'styled-components';
import { throttle } from 'throttle-debounce';
import { useInfiniteScroll } from '../../lib/infiniteScroll.js';
import { setTestId } from '../../lib/testId.js';
import { useTranslate } from '../../lib/translate.js';
import { Badge } from '../badge/Badge.js';
import { useQueryChildComponents } from '../base/SystemComponent.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { CSSDefaultInputField } from '../fields/common.js';
import { FIELD_ERROR_DATA_ATTRIBUTE } from '../fields/FieldError.js';
import { Icon } from '../icon/Icon.js';
import { Box } from '../layout/Box.js';
import '../layout/BulletList.js';
import { Column } from '../layout/Column.js';
import { Columns } from '../layout/Columns.js';
import '../layout/Hidden.js';
import { Inline } from '../layout/Inline.js';
import '../layout/Stack.js';
import '../layout/VisuallyHidden.js';
import { LoadingSpinnerOverlay, LoadingSpinner } from '../loading/LoadingSpinner.js';
import { getColor } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { CheckboxInput } from './CheckboxInput.js';
import { CSSDefaultPopover, setReadOnlyDataAttribute, InnerInputLabel } from './InputCommon.js';
import { SelectOption } from './SelectInput.js';

const MIN_INPUT_WIDTH_REM = 13;
const SelectPopover = styled(ListboxPopover)`
  &[data-reach-listbox-popover] {
    ${CSSDefaultPopover}
    font-size: 0.875rem;
    border-color: ${(props)=>getColor("slateGrey10", props.theme)};
    /* Ensures content does not extend past bounds of input */
    ${(props)=>props.$width === "fill" && `min-width: ${MIN_INPUT_WIDTH_REM - 1}rem;`}
  }
  &[data-reach-listbox-popover] > [data-hpy-web-select-field-listbox-list] {
    max-height: 14rem;
    overflow: auto;
  }
`;
const StyledListboxOption = styled(ListboxOption)`
  &[data-reach-listbox-option] {
    height: 100%;
    min-height: 2.5rem;

    justify-content: center;
    display: flex;
    flex-direction: column;
    position: relative;

    font-size: 0.875rem;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0;
    & > label {
      padding: 0.5rem 0.75rem;
    }
    & > label [${Columns.dataId}] > [${Column.dataId}] [${Text.dataId}] {
      white-space: break-spaces;
    }

    &[aria-selected='true'],
    &:hover {
      cursor: pointer;

      background: ${COLOR_PALETTE.slateGrey5};
      color: ${(props)=>getColor("context", props.theme)};
    }
  }
`;
const MultiSelectOption = ({ label , value , testId ="X" , checked =false , children , onChange , ...props })=>{
    return React__default.createElement(StyledListboxOption, {
        "data-hpy-web-multiselect-option": true,
        ...setTestId("MultiSelectOption-" + testId),
        value: value,
        label: label,
        onMouseUp: (e)=>e.preventDefault(),
        onClick: (e)=>e.preventDefault()
    }, // eslint-disable-next-line jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    React__default.createElement("label", {
        style: {
            width: "100%"
        },
        onClick: (event)=>event.stopPropagation()
    }, React__default.createElement(Columns, {
        gap: 0.5
    }, React__default.createElement(Column, {
        span: "content"
    }, React__default.createElement(CheckboxInput, {
        testId: "MultiSelectOption-" + value,
        checked: checked,
        onChange: ()=>onChange(value)
    })), React__default.createElement(Column, {
        span: 1
    }, !!children ? children : React__default.createElement(Box, {
        width: "fill",
        margin: [
            0.375,
            0,
            0
        ]
    }, React__default.createElement(Text, {
        weight: "semibold",
        size: "medium",
        color: "slateGreyDark60",
        align: "left"
    }, label))))));
};
MultiSelectOption.dataId = "data-hpy-web-multiselect-option";
MultiSelectOption.displayName = "MultiSelectOption";
const StyledSearchInput = styled.input`
  &[data-hpy-web-select-search-input] {
    position: relative;
    display: flex;
    cursor: text;
    align-items: flex-start;
    padding: 1rem 1rem 1rem 2.5rem;
    border-radius: 0;
    width: 100%;
    min-width: ${MIN_INPUT_WIDTH_REM}rem;
    height: 2.75rem;
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 500;
    border: none;
    border-bottom: 1px solid ${(props)=>getColor("slateGrey10", props.theme)};
    color: ${(props)=>getColor("slateGrey80", props.theme)};

    :focus {
      outline: none;
    }
  }
`;
const StyledIcon = styled(Icon)`
  position: absolute;
  left: 0.75rem;
  top: 0.75rem;
`;
const SelectSearchInput = ({ testId ="X" , throttleMS =600 , ...props })=>{
    const [value, setValue] = useState("");
    const t = useTranslate();
    const throttleOnChange = useCallback(// eslint-disable-next-line @typescript-eslint/no-empty-function
    throttle(throttleMS, false, props?.onChange !== undefined ? props.onChange : ()=>{}), [
        throttleMS
    ]);
    const onChange = useCallback((e)=>{
        setValue(e.target.value);
        if (props.onChange) {
            throttleOnChange(e.target.value);
        }
    }, [
        props.onChange,
        throttleOnChange
    ]);
    const ref = useRef();
    const onKeyDown = useCallback((e)=>{
        if (e.key === " ") {
            // Handle the space key locally and do not allow it to propagate to the listbox wrapper
            // Without this, the listbox will try to use the space character to "select" the current item, and will
            // block the character from appearing in the input.
            e.stopPropagation();
        }
    }, []);
    const listBox = useListboxContext();
    useEffect(()=>{
        let timer = undefined;
        if (listBox.isExpanded) {
            // Manually focus the search input on open, as reach-ui attempts to focus the first available select option
            // instead. This requires a slight delay so as to override any reach-ui focus controls.
            timer = setTimeout(()=>{
                ref?.current?.focus();
            }, 100);
        } else {
            // Select popover has been closed, clear the search value
            setValue("");
            if (props.onChange) {
                props.onChange("");
            }
        }
        return ()=>{
            if (timer !== undefined) {
                clearTimeout(timer);
            }
        };
    }, [
        listBox.isExpanded
    ]);
    return React__default.createElement(Box, null, React__default.createElement(StyledSearchInput, {
        "data-hpy-web-select-search-input": true,
        ...props,
        ...setTestId(`MultiSelectInput-Search-${testId}`),
        ref: ref,
        placeholder: t("system:SelectInput.search"),
        value: value,
        onChange: onChange,
        onKeyDown: onKeyDown
    }), React__default.createElement(StyledIcon, {
        icon: "search",
        size: "xsmall"
    }));
};
SelectSearchInput.dataId = "data-hpy-web-select-search-input";
SelectSearchInput.displayName = "SelectSearchInput";
const StyledListboxInput = styled(ListboxInput)`
  &[data-hpy-web-select-field-input-base] [data-reach-listbox-button] {
    ${CSSDefaultInputField}
    position: relative;
    display: flex;
    cursor: pointer;
    padding: 1.375rem 2rem 0.5rem 1rem;
    align-items: flex-start;
    min-width: ${MIN_INPUT_WIDTH_REM}rem;
    min-height: 3.25rem;

    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:hover,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:focus {
      color: inherit;
    }
  }
  &[data-hpy-web-select-field-input-chip] [data-reach-listbox-button] {
    min-width: ${MIN_INPUT_WIDTH_REM}rem;
  }

  &[data-hpy-web-select-field-input-base][data-state='expanded'] [data-reach-listbox-button],
  &[data-hpy-web-select-field-input-base][data-state='expanded'] [data-reach-listbox-button]:hover,
  &[data-hpy-web-select-field-input-base] [data-reach-listbox-button]:focus {
    outline: none;
    border-color: ${(props)=>getColor("context", props.theme)};
  }

  &[data-hpy-web-select-field-input-base]
    [data-reach-listbox-button][data-hpy-web-select-input-input-has-value]
    [data-hpy-web-select-input-input-inner-label] {
    transform-origin: 4rem -5rem;
  }
  &[data-hpy-web-select-field-input-base] [data-reach-listbox-button] [data-hpy-web-select-input-input-inner-label] {
    transform-origin: 4rem -5rem;
    transform: scale(1) translateX(-1rem) translateY(-0.5rem);
  }
`;
const StyledListBoxArrow = styled(ListboxArrow)`
  position: absolute;
  top: 0;
  margin-top: 2px;
  right: -1rem;
  display: block;
  width: 0px;
  height: 0px;
  content: '';
  transform: rotate(0deg);
  border-top: 5px solid ${COLOR_PALETTE.slateGrey100};
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  transition: transform 0.2s ease 0s, -webkit-transform 0.2s ease 0s;

  &[data-expanded] {
    transform: rotate(180deg);
  }
`;
const StyledNoResults = styled(Box)`
  height: 2.5rem;
  padding: 0 1rem;
  justify-content: center;
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  color: ${COLOR_PALETTE.slateGrey40};
`;
const SelectedText = styled(Text)`
  font-size: 0.875rem;
`;
const StyledLoadingSpinnerOverlay = styled(LoadingSpinnerOverlay)`
  margin-top: 2.75rem;
  padding-bottom: 2.75rem;
`;
const StyledBadge = styled(Badge)`
  height: 1.5rem;
  min-width: 1.5rem;
  border-radius: 50%;
  text-align: center;
  padding: 0.475rem 0;
  margin: -0.5rem 0 0 -0.25rem;
`;
const useSelectedLabel = (children, value)=>{
    const selectedLabel = useRef();
    return useMemo(()=>{
        if (!value) {
            selectedLabel.current = "";
        } else {
            const labels = [];
            value.forEach((selected)=>{
                const labelChild = Children.toArray(children).find((child)=>{
                    const childOption = child;
                    return childOption && childOption.props.value === selected && childOption.props.label;
                });
                if (labelChild?.props?.label) {
                    labels.push(labelChild.props.label);
                }
            });
            selectedLabel.current = labels.join(", ");
        }
        return selectedLabel.current;
    }, [
        children,
        value
    ]);
};
/**
 * Multi Select Input.
 * Accepts `SelectOption`s as child components. Displays a count of the selected items and a preview of what the selected items are.
 *
 * @example
 * <MultiSelectInput name="chicken">
 *  <SelectOption label="Egg" value="egg" />
 *  <SelectOption label="Duck" value="duck" />
 * </MultiSelectInput>
 */ const MultiSelectInput = forwardRef(({ children , label , width ="fill" , readOnly =false , testId ="X" , throttleMS =600 , loading =false , loadingMore =false , hasNextPage =false , value =[] , onLoadMore =undefined , onSearch , onFocus , onOpen , ...props }, ref)=>{
    const t = useTranslate();
    const [searchText, setSearchText] = useState("");
    const [options] = useQueryChildComponents(children, [
        SelectOption
    ]);
    const scroll = useInfiniteScroll({
        loading: loadingMore,
        hasNextPage,
        onLoadMore: onLoadMore ?? (()=>null),
        // Arbitrary margin so that items are loaded before 'bottom' of scroll is reached
        containerMargin: "0px 0px 150px 0px"
    });
    // Delay the loading exit, so that the Select dropdown has enough time to rerender before 'loading' is complete
    const transition = useTransition(loading, {
        from: {
            opacity: 0
        },
        enter: {
            opacity: 1
        },
        leave: {
            opacity: 0
        },
        trail: 100
    });
    const [initialSelectedOptions, setInitialSelectedOptions] = useState([]);
    const handleOnOpen = useCallback(()=>{
        const selectedOptions = [];
        value.forEach((v)=>{
            const initialOption = options.find((o)=>o.props.value === v);
            if (initialOption) {
                selectedOptions.push(initialOption);
            }
        });
        // Cache the options that were selected when the list was opened.
        setInitialSelectedOptions(selectedOptions);
        onOpen && onOpen();
    // Ignore changes to the options
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        onOpen,
        value
    ]);
    const handleOnKeyDown = useCallback((event)=>{
        // Capture focus + space key keyboard usage of select input
        if (event.key === " ") {
            handleOnOpen();
        }
    }, [
        handleOnOpen
    ]);
    // Filter the options so that duplicate entries are removed from the list
    // This may occur if the caller provides the selected items alongside a query-based list.
    const filteredOptions = useMemo(()=>{
        const seenOptions = {};
        const initialOptions = [];
        const filtered = [];
        if (!searchText) {
            // Take the initial selected from the start of the list
            initialSelectedOptions.forEach((i)=>{
                if (i.props?.value) {
                    initialOptions.push(i);
                    seenOptions[i.props.value] = true;
                }
            });
            // Assume that any duplicated values are provided with the selected items first, followed by regular paginated results
            // Add entries from the bottom up, so that initially selected items can be shown above the rest
            options.reverse().forEach((option)=>{
                if (option.props.value !== undefined) {
                    if (!seenOptions[option.props.value]) {
                        filtered.push(option);
                        seenOptions[option.props.value] = true;
                    }
                }
            });
            filtered.reverse();
        } else {
            const searchFiltered = [
                ...options
            ];
            // Remove an instance of a selected item for each option
            value.forEach((selected)=>{
                let selectedIndex = -1;
                // If the value was initially selected, get rid of the lower instances
                if (initialSelectedOptions.map((o)=>o.props.value).includes(selected)) {
                    const reversed = [
                        ...searchFiltered
                    ].reverse();
                    selectedIndex = reversed.findIndex((option)=>option.props.value === selected);
                    if (selectedIndex >= 0) {
                        searchFiltered.splice(searchFiltered.length - 1 - selectedIndex, 1);
                    }
                } else {
                    // Otherwise, remove the top instances
                    selectedIndex = searchFiltered.findIndex((option)=>option.props.value === selected);
                    searchFiltered.splice(selectedIndex, 1);
                }
            });
            return searchFiltered;
        }
        return [
            ...initialOptions,
            ...filtered
        ];
    }, [
        options,
        initialSelectedOptions,
        searchText,
        value
    ]);
    const selectedLabel = useSelectedLabel(options, value);
    const handleOnChange = useCallback((optionValue)=>{
        if (props.onChange && optionValue) {
            if (value.includes(optionValue)) {
                props.onChange(value.filter((v)=>v !== optionValue));
            } else {
                props.onChange([
                    ...value,
                    optionValue
                ]);
            }
        }
    }, [
        props.onChange,
        value
    ]);
    const handleSearch = useCallback((searchText)=>{
        setSearchText(searchText);
        onSearch && onSearch(searchText);
    }, [
        onSearch
    ]);
    return React__default.createElement(StyledListboxInput, {
        "data-hpy-web-select-input": true,
        "data-hpy-web-select-field-input-base": true,
        ...setTestId("MultiSelectInput-" + testId),
        ...props,
        ref: ref,
        value: undefined,
        as: undefined,
        onFocus: onFocus
    }, (listbox)=>React__default.createElement(React__default.Fragment, null, React__default.createElement(ListboxButton, {
            ...selectedLabel ? {
                "data-hpy-web-select-input-input-has-value": true
            } : {},
            ...setTestId("MultiSelectInput-Button-" + testId),
            disabled: readOnly,
            ...setReadOnlyDataAttribute(readOnly),
            as: undefined,
            onClick: handleOnOpen,
            onKeyDown: handleOnKeyDown
        }, React__default.createElement(Columns, {
            gap: 0.725,
            height: "fill"
        }, !!value?.length && React__default.createElement(Column, {
            span: "content"
        }, React__default.createElement(StyledBadge, {
            size: "medium"
        }, value.length)), React__default.createElement(Column, null, React__default.createElement(SelectedText, {
            truncate: true,
            align: "left"
        }, selectedLabel), React__default.createElement(StyledListBoxArrow, null, "\xa0"), React__default.createElement(InnerInputLabel, {
            "data-hpy-web-select-input-input-inner-label": true,
            size: "medium",
            weight: "semibold"
        }, label)))), React__default.createElement(SelectPopover, {
            ...setTestId("Popover-" + testId),
            portal: true,
            "$width": width,
            onKeyDown: (e)=>{
                if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    const optionValue = listbox.highlightedOptionRef?.current?.getAttribute("data-value");
                    if (optionValue) {
                        handleOnChange(optionValue);
                        e.preventDefault();
                    }
                }
            }
        }, typeof onSearch === "function" && React__default.createElement(SelectSearchInput, {
            testId: testId,
            throttleMS: throttleMS,
            onChange: handleSearch
        }), React__default.createElement(ListboxList, {
            ref: scroll.containerRef,
            "data-hpy-web-select-field-listbox-list": true
        }, !listbox.isExpanded || filteredOptions.length === 0 ? React__default.createElement(StyledNoResults, {
            ...setTestId("MultiSelectInput-NoResults-" + testId)
        }, t("system:SelectInput.no_options")) : filteredOptions.map((option)=>React__default.createElement(MultiSelectOption, {
                key: option.props.value,
                testId: option.props.testId,
                checked: value.includes(option.props.value ?? ""),
                label: option.props.label,
                value: option.props.value ?? "",
                onChange: ()=>handleOnChange(option.props.value ?? "")
            }, option.props.children)), hasNextPage && React__default.createElement(Box, {
            ref: scroll.scrollElementRef,
            width: "fill",
            minHeight: 3,
            as: "li"
        }, React__default.createElement(Inline, {
            align: "center",
            alignY: "center",
            width: "fill"
        }, loadingMore && React__default.createElement(LoadingSpinner, {
            size: "small",
            testId: "MultiSelectInput-LoadMore-" + testId
        })))), transition((_, show)=>show && React__default.createElement(Box, null, React__default.createElement(StyledLoadingSpinnerOverlay, {
                size: "medium",
                bgOverlay: "partial",
                testId: "MultiSelectInput-Load-" + testId
            }))))));
});
MultiSelectInput.dataId = "data-hpy-web-multi-select-input";
MultiSelectInput.displayName = "MultiSelectInput";

export { MultiSelectInput };
